/**
 * Row selection for a table with a select-all box in its header.
 *
 * Selection outlives a page turn and a filter change, so the select-all box
 * answers for the rows ON SCREEN while the selection bar counts everything
 * picked. That split is the whole design, and it is why these take the visible
 * ids and the selected ids as two separate lists.
 */

export type HeaderCheck = 'none' | 'some' | 'all'

/** What the select-all box shows for the rows on screen. */
export function headerCheckState(visibleIds: readonly string[], selected: readonly string[]): HeaderCheck {
  if (visibleIds.length === 0) return 'none'
  const picked = new Set(selected)
  const on = visibleIds.filter((id) => picked.has(id)).length
  if (on === 0) return 'none'
  return on === visibleIds.length ? 'all' : 'some'
}

/**
 * The select-all box, pressed.
 *
 * When every row on screen is already picked it lets them go, and otherwise it
 * picks every one of them. Rows picked on other pages are left exactly as they
 * were either way, because the box only ever spoke for this page.
 */
export function toggleAllVisible(visibleIds: readonly string[], selected: readonly string[]): string[] {
  const visible = new Set(visibleIds)
  if (headerCheckState(visibleIds, selected) === 'all') return selected.filter((id) => !visible.has(id))
  const next = [...selected]
  const picked = new Set(selected)
  for (const id of visibleIds) if (!picked.has(id)) next.push(id)
  return next
}

/** One row's box, pressed. */
export function toggleSelected(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((other) => other !== id) : [...selected, id]
}
